// custom-ui/ContentListShell.tsx
// The ONE list-page shell for the employer's "content" surfaces (Rekrytering /
// Moduler). Owns the toolbar (optional filter tabs on the left + a primary
// "Skapa X" action on the right), the elevated container, and the loading /
// empty states — so both list pages share one layout. Rows (ContentEntityRow)
// go in `children`.
import React from 'react';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';
import { LoadingState } from './empty-state';

interface ContentListShellProps {
    /** Left side of the toolbar — status/filter tabs. Omit when there are none. */
    filters?: React.ReactNode;
    /** Right side of the toolbar — the primary "Skapa X" action. */
    action?: React.ReactNode;
    isLoading?: boolean;
    loadingMessage?: string;
    /** True when there are no items at all — renders `emptyState` full-bleed
     *  (no toolbar), matching the first-run experience. */
    isEmpty?: boolean;
    emptyState?: React.ReactNode;
    /** The rows. When a filter matches nothing (but items exist), pass a small
     *  inline "no results" node here instead. */
    children?: React.ReactNode;
}

export const ContentListShell: React.FC<ContentListShellProps> = ({
    filters,
    action,
    isLoading,
    loadingMessage = 'Laddar...',
    isEmpty,
    emptyState,
    children,
}) => {
    if (isLoading) {
        return <LoadingState message={loadingMessage} />;
    }

    if (isEmpty) {
        return <div className={NEXILINK_SPACING}>{emptyState}</div>;
    }

    const hasToolbar = !!filters || !!action;

    return (
        <div className={NEXILINK_SPACING}>
            <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                {hasToolbar && (
                    <div className="p-4 flex items-center justify-between gap-4 flex-wrap border-b border-gray-50">
                        <div className="flex gap-2 flex-wrap">{filters}</div>
                        {action}
                    </div>
                )}
                <div className="p-2 divide-y divide-gray-50">{children}</div>
            </div>
        </div>
    );
};

export default ContentListShell;
